/**
 * London Smoke & Cure — user role tokens (comma-separated string in Firestore `users.role`).
 * Storage keeps Title Case so existing ProtectedRoute / nav checks keep working.
 */

/** Assignable internal + customer roles (multi-select). */
export const USER_ROLE_OPTIONS = [
    { id: 'Admin', label: 'Admin' },
    { id: 'ProductionAdmin', label: 'Production Admin' },
    { id: 'Production', label: 'Production' },
    { id: 'Packer', label: 'Packer' },
    { id: 'Staff', label: 'Staff' },
    { id: 'Rider', label: 'Rider' },
    { id: 'Customer', label: 'Customer' },
];

const CANONICAL_BY_LOWER = Object.fromEntries(
    USER_ROLE_OPTIONS.map((o) => [o.id.toLowerCase(), o.id])
);

/**
 * Parse Firestore `role` (string or array) into canonical Title Case tokens.
 * Unknown tokens are kept with original trim (so legacy values are not dropped).
 * @param {unknown} roleField
 * @returns {string[]}
 */
export function parseUserRoles(roleField) {
    let raw = [];
    if (Array.isArray(roleField)) {
        raw = roleField.map((r) => String(r || '').trim()).filter(Boolean);
    } else if (typeof roleField === 'string') {
        raw = roleField
            .split(',')
            .map((r) => r.trim())
            .filter(Boolean);
    }
    const out = [];
    const seen = new Set();
    for (const token of raw) {
        const canonical = CANONICAL_BY_LOWER[token.toLowerCase()] || token;
        const key = canonical.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(canonical);
    }
    return out;
}

/**
 * @param {string[]|unknown} roles
 * @returns {string} Firestore `role` string
 */
export function serializeUserRoles(roles) {
    const list = parseUserRoles(Array.isArray(roles) ? roles.join(',') : roles);
    return list.join(',');
}

/**
 * @param {unknown} roleField
 * @param {string|string[]} roleNames
 * @returns {boolean}
 */
export function userHasAnyRole(roleField, roleNames) {
    const have = new Set(parseUserRoles(roleField).map((r) => r.toLowerCase()));
    const want = (Array.isArray(roleNames) ? roleNames : [roleNames])
        .map((r) => String(r || '').trim().toLowerCase())
        .filter(Boolean);
    return want.some((r) => have.has(r));
}

/**
 * Toggle a role id in a role string.
 * @param {unknown} roleField
 * @param {string} roleId
 * @param {boolean} enabled
 * @returns {string}
 */
export function setUserRoleEnabled(roleField, roleId, enabled) {
    const canonical = CANONICAL_BY_LOWER[String(roleId || '').toLowerCase()] || String(roleId || '').trim();
    if (!canonical) return serializeUserRoles(roleField);
    const set = new Set(parseUserRoles(roleField).map((r) => r.toLowerCase()));
    const key = canonical.toLowerCase();
    if (enabled) set.add(key);
    else set.delete(key);
    const next = [...set].map((k) => CANONICAL_BY_LOWER[k] || k);
    return serializeUserRoles(next);
}
